import { Box, Stack, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { useWatch } from 'react-hook-form';

import { Button } from '@/components/form/baseInputs/Button';
import { ArrowRightIcon } from '@/components/icons/ArrowRightIcon';
import { ChevronLeftIcon } from '@/components/icons/ChevronLeftIcon';
import { Microphone02Icon } from '@/components/icons/Microphone02Icon';
import { Stars01FilledIcon } from '@/components/icons/Stars01FilledIcon';
import { DiagramDisplayCard } from '@/components/layout/DiagramDisplayCard/DiagramDisplayCard';
import { useFormContext } from '@/components/react-hook-form';
import { LargeChatComposer } from '@/modules/common/Chat/components/LargeChatComposer';
import { SelectedTemplate } from '@/modules/common/Chat/components/SelectedTemplate';
import { HouseholdChatDictationControl } from '@/modules/household/chat/components/HouseholdChatDictationControl';
import type { DictationState } from '@/modules/household/chat/hooks/useHouseholdChatDictation';
import { COLORS } from '@/styles/tokens/colors';
import { NoDocumentsOnboardingPromptType } from '@/types/schema';

import { ClientSetupFormShape } from '../clientSetup/ClientSetup.type';
import { getPropertyLaw, type PropertyLaw } from './noDocumentsPlanPrompts';
import {
  JOINT_TRUST_PLAN_TEMPLATES,
  type PlanTemplate,
  SEPARATE_TRUSTS_PLAN_TEMPLATES,
  SINGLE_GRANTOR_TEMPLATES,
  SURVIVING_SPOUSE_TEMPLATE,
} from './noDocumentsPlanTemplates';

/**
 * The property law of the primary client's state is only a guideline. Show
 * the A/B and A/B/C structures that usually apply to that state first.
 */
function getTemplates(
  isTwoGrantor: boolean,
  propertyLaw: PropertyLaw
): PlanTemplate[] {
  if (!isTwoGrantor) {
    return SINGLE_GRANTOR_TEMPLATES;
  }

  if (propertyLaw === 'community property') {
    return [
      ...JOINT_TRUST_PLAN_TEMPLATES,
      SURVIVING_SPOUSE_TEMPLATE,
      ...SEPARATE_TRUSTS_PLAN_TEMPLATES,
    ];
  }

  return [
    ...SEPARATE_TRUSTS_PLAN_TEMPLATES,
    SURVIVING_SPOUSE_TEMPLATE,
    ...JOINT_TRUST_PLAN_TEMPLATES,
  ];
}

const DEFAULT_PROMPT =
  'e.g. One joint revocable trust. At the first death it splits into a survivor’s trust, a family trust funded to the federal exemption, and a QTIP for the excess. Everything to the two kids equally at the second death. All direct holdings should flow into the Revocable Trust via a pour-over will.';
const SELECTED_TEMPLATE_PROMPT =
  'Please include additional information that describes the plan structure and recipients, as well as basic information about the client’s balance sheet. The more context you provide, the more accurate the plan construction will be. Click “Build household” when you’re ready to proceed.';

export interface NoDocumentsPlanBuildInput {
  advisorContext: string;
  templateId?: NoDocumentsOnboardingPromptType;
}

interface NoDocumentsPlanSectionProps {
  onBack: () => void;
  onBuildPlan: (input: NoDocumentsPlanBuildInput) => void;
}

/** Template-selection section rendered inside the onboarding trowser. */
export function NoDocumentsPlanSection({
  onBack,
  onBuildPlan,
}: NoDocumentsPlanSectionProps) {
  const { control } = useFormContext<ClientSetupFormShape>();
  const clientOne = useWatch({ control, name: 'clientOne' });
  const clientTwo = useWatch({ control, name: 'clientTwo' });
  const isTwoGrantor = Boolean(clientTwo?.firstName || clientTwo?.lastName);
  const propertyLaw = getPropertyLaw(clientOne.stateCode);
  const templates = getTemplates(isTwoGrantor, propertyLaw);
  const [selectedTemplateValue, setSelectedTemplateValue] =
    useState<NoDocumentsOnboardingPromptType | null>(null);
  const selectedTemplate =
    templates.find(({ value }) => value === selectedTemplateValue) ?? null;
  const [message, setMessage] = useState('');
  const [dictationState, setDictationState] = useState<DictationState>('idle');

  const appendTranscript = useCallback((transcript: string) => {
    setMessage((current) => {
      const separator = current && !current.endsWith(' ') ? ' ' : '';
      return `${current}${separator}${transcript}`;
    });
  }, []);

  return (
    <Stack height="100%" bgcolor={COLORS.PRIMITIVES.WHITE}>
      <Stack
        component="header"
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          height: 60,
          minHeight: 60,
          flexShrink: 0,
          px: 2,
          borderBottom: `1px solid ${COLORS.GRAY[200]}`,
        }}
      >
        <Box width={450}>
          <Button
            variant="transparent"
            size="sm"
            startIcon={ChevronLeftIcon}
            iconProps={{ 'aria-hidden': true }}
            onClick={onBack}
          >
            Back to documents
          </Button>
        </Box>
        <Stack alignItems="center" width={300}>
          <Typography variant="title-s" noWrap>
            Create waterfall without documents
          </Typography>
        </Stack>
        <Box width={450} />
      </Stack>

      <Stack sx={{ flex: 1, overflowY: 'auto', px: 8, pt: 4.5, pb: 8 }}>
        <Stack width="100%" maxWidth={1312} alignSelf="center" gap={5}>
          <Stack gap={1} maxWidth={990}>
            <Typography variant="title-l">
              Start from a plan structure
            </Typography>
            <Typography variant="body-s" sx={{ color: COLORS.TEXT.SECONDARY }}>
              Pick the pattern closest to the client&apos;s plan, or describe it
              in a sentence. The assistant starts with the household details and
              selected pattern already loaded.
            </Typography>
          </Stack>

          <Box width="100%" maxWidth={1144} alignSelf="center">
            <LargeChatComposer
              allowEmptySubmit={Boolean(selectedTemplate)}
              autoFocusOnMount={false}
              buttonEndIcon={ArrowRightIcon}
              buttonLabel="Build household"
              buttonVariant={
                selectedTemplate || message.trim() ? 'prominent' : 'primary'
              }
              emphasized={Boolean(selectedTemplate)}
              inputStartAdornment={
                <Stars01FilledIcon
                  data-testid="no-documents-plan-composer-sparkle"
                  size={16}
                  color={COLORS.ORANGE[500]}
                  aria-hidden
                />
              }
              leadingAction={
                selectedTemplate ? (
                  <SelectedTemplate
                    title={selectedTemplate.title}
                    onRemove={() => setSelectedTemplateValue(null)}
                  />
                ) : undefined
              }
              minRows={4}
              onSubmit={() =>
                onBuildPlan({
                  advisorContext: message,
                  templateId: selectedTemplate?.value,
                })
              }
              onValueChange={setMessage}
              placeholder={
                selectedTemplate ? SELECTED_TEMPLATE_PROMPT : DEFAULT_PROMPT
              }
              submitDisabled={
                (!selectedTemplate && !message.trim()) ||
                dictationState !== 'idle'
              }
              testId="no-documents-plan-composer"
              trailingAction={
                <HouseholdChatDictationControl
                  dictationTarget="plan description"
                  idleControlSize={24}
                  idleIcon={Microphone02Icon}
                  idleIconSize={24}
                  onStateChange={setDictationState}
                  onTranscript={appendTranscript}
                />
              }
              value={message}
            />
          </Box>

          <Stack gap={2}>
            <Typography variant="title-m">Choose a template</Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: 2.5,
              }}
            >
              {templates.map(({ Illustration, ...template }) => {
                const isSelected = selectedTemplateValue === template.value;
                return (
                  <DiagramDisplayCard<NoDocumentsOnboardingPromptType>
                    bodySx={{ p: 2 }}
                    key={template.title}
                    icon={Illustration}
                    onClick={setSelectedTemplateValue}
                    selected={isSelected}
                    value={template.value}
                    variant="default"
                  >
                    <Stack gap={1.5}>
                      <Typography variant="title-s">
                        {template.title}
                      </Typography>
                      <Typography
                        variant="body-xs"
                        sx={{ color: COLORS.TEXT.SECONDARY }}
                      >
                        {template.description}
                      </Typography>
                      <Stack gap={0.5}>
                        <Typography variant="title-xs">
                          Holding assumptions
                        </Typography>
                        <Typography
                          variant="body-xs"
                          sx={{ color: COLORS.TEXT.SECONDARY }}
                        >
                          {template.assumptions}
                        </Typography>
                      </Stack>
                    </Stack>
                  </DiagramDisplayCard>
                );
              })}
            </Box>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}
